import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { useChatProfileShallowSelector } from 'components/AskDelaChat/ChatProfileStore'
import { useWorkspace2Store } from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback } from 'react'
import { useElementList } from '../../../ActionBarStore'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    findAncestors,
    getChildElements,
} from '../../../utils/hoveredActionUtils'
import {
    useContributorRestrictionDialogActions,
    usePTActivationDialogActions,
    usePTActivationStore,
    usePTDeactivationDialogActions,
} from '../store'
import {
    checkParagraphLinkedToInactivePT,
    collectAllChildrenAndAncestors,
    collectPTLinkedContent,
    extractTextBeforeColon,
    findInactivePTFromContentItem,
    getPTElementNodes,
} from '../utils'
import { useArticleSync } from './useArticleSync'
import { useCollapsedArticleChildFetcher } from './useCollapsedArticleChildFetcher'
import { useProductTypeContentActivation } from './useProductTypeContentActivation'
import { usePTAddition } from './usePTAddition'
import { usePTContentActivation } from './usePTContentActivation'
import { usePTContentDeactivation } from './usePTContentDeactivation'
import { usePTKeyParagraphValidator } from './usePTKeyParagraphValidator'
import { usePTProjectFilterCache } from './usePTProjectFilterCache'

/**
 * Hook that handles the entire PT activation and deactivation flow:
 *
 * **Activation Flow:**
 * 1. Checks if paragraph is linked to an inactive Product Type
 * 2. Opens warning dialog if inactive PT detected
 * 3. Adds PT to project when user proceeds
 * 4. Activates all content items linked to the PT with optimistic updates
 * 5. Handles both Parent→Child and Child→Parent activation flows (with optional onComplete callback to control activation scope)
 *
 * **Deactivation Flow:**
 * 1. Checks if content is linked to an active Product Type
 * 2. Opens warning dialog if active PT detected
 * 3. Deactivates only the specific content item (not the PT) when user proceeds
 * 4. Does not deactivate the PT itself, allowing user to keep the PT active but remove it from specific content
 * 5. Handles both child→parent and parent→child deactivation scenarios (with shouldWarnPTDeactivation utility to check if warning needed without opening dialog)
 */
export const usePTActivationHandler = () => {
    const role = useChatProfileShallowSelector((state) => state.chatUser?.role)
    const {
        openDialog: openPTActivationDialog,
        closeDialog: closePTActivationDialog,
    } = usePTActivationDialogActions()
    const {
        openDeactivationDialog: openPTDeactivationDialog,
    } = usePTDeactivationDialogActions()
    const {
        openContributorRestrictionDialog,
    } = useContributorRestrictionDialogActions()
    const closeProgressDialog = usePTActivationStore(
        (state) => state.closeProgressDialog
    )
    const elementList = useElementList()

    // Use smaller focused hooks
    const { addPTsToProject } = usePTAddition()
    const { activateContentWithRollback } = usePTContentActivation()
    const { deactivateSingleContent } = usePTContentDeactivation()
    const {
        filterOutBODNodes,
        syncPTContentToActivationList,
    } = useProductTypeContentActivation()
    const { syncArticlesToActivationList } = useArticleSync()
    const { fetchChildrenForArticle } = useCollapsedArticleChildFetcher()

    // PT key paragraph validator hook with cache support
    const { isDirectPTKeyParagraph } = usePTKeyParagraphValidator()
    const {
        getPTElementNodesWithCache,
        getPTElementNodesWithBulkCache,
    } = usePTProjectFilterCache()
    /**
     * PT Cache: Handles "Added To Project" filter scenario
     *
     * Problem: When "Added To Project" filter is ON, elementList only contains
     * elements added to the project. This breaks PT detection for inactive PTs
     * that aren't in the project yet.
     *
     * Solution: This cache proactively fetches ALL PTs for the current family
     * (ignoring filter) when a family is selected. During PT detection, we check
     * the cache first before relying on elementList.
     *
     * Usage:
     * - addPTFromCacheIfNeeded(ptName) - Returns cached PT if not in elementList
     * - Used in detectInactivePTs and detectInactiveParentPTs
     *
     * @see usePTProjectFilterCache.ts for implementation details
     * @see useElementToTreeMapper.ts (lines 186-205) for cache population logic
     * @see usePTKeyParagraphValidator.getPTElementNodesWithCache for cache usage
     */

    /**
     * Unified PT detection function - detects inactive PTs that need to be added
     * Returns array of inactive PT element nodes (empty array if none found)
     */
    const detectInactivePTs = useCallback(
        (
            contentItem: Partial<ContentItemViewNodesType>,
            allContentNodes: Partial<ContentItemViewNodesType>[],
            selectedProjectElementNode?: Partial<ProjectElementNode>
        ): ProjectElementNode[] => {
            // Early validation (from validatePTCheckContext)
            if (!selectedProjectElementNode?.isInProject) {
                return []
            }

            const inactivePTMap = new Map<string, ProjectElementNode>()

            // CASE 1: Clicked item itself is a PT-keyed paragraph.
            // Accumulate result but do NOT return early — its children may also
            // contain nested PT-keyed paragraphs that need to be checked.
            const paragraphText = contentItem.elements?.[0]?.text
            const ptElementNodes = getPTElementNodesWithCache(contentItem)

            if (
                paragraphText &&
                contentItem.textType === 'Paragraph' &&
                ptElementNodes.length > 0
            ) {
                const ptCheck = checkParagraphLinkedToInactivePT(
                    paragraphText,
                    ptElementNodes
                )

                if (ptCheck.hasInactivePT && ptCheck.ptElementNode) {
                    inactivePTMap.set(
                        ptCheck.ptElementNode.id,
                        ptCheck.ptElementNode
                    )
                }
            }

            // CASE 2: Collect all descendants and check each for inactive PT links.
            // Re-root at children whose text has "Name: ..." format so nested
            // PT-keyed paragraphs at any depth are fully traversed.
            const allDescendants: Partial<ContentItemViewNodesType>[] = []
            const visitedIds = new Set<string>()

            const collectDescendants = (
                node: Partial<ContentItemViewNodesType>
            ) => {
                getChildElements(node, allContentNodes).forEach((child) => {
                    if (!child.id || visitedIds.has(child.id)) return
                    visitedIds.add(child.id)
                    allDescendants.push(child)
                    // Recurse into children whose text matches PT-keyed format
                    if (child.elements?.[0]?.text?.includes(':')) {
                        collectDescendants(child)
                    }
                })
            }
            collectDescendants(contentItem)

            if (allDescendants.length > 0) {
                const fullPTElementNodes = getPTElementNodesWithBulkCache(
                    allDescendants
                )
                allDescendants.forEach((childItem) => {
                    const inactivePT = findInactivePTFromContentItem(
                        childItem,
                        fullPTElementNodes
                    )
                    if (inactivePT) {
                        inactivePTMap.set(inactivePT.id, inactivePT)
                    }
                })
            }

            return Array.from(inactivePTMap.values())
        },
        [getPTElementNodesWithCache, getPTElementNodesWithBulkCache]
    )

    /**
     * Detects inactive PTs in parent ancestors (child → parent flow)
     * Returns array of inactive PT element nodes found in ancestor chain (empty array if none found)
     */
    const detectInactiveParentPTs = useCallback(
        (
            contentItem: Partial<ContentItemViewNodesType>,
            allContentNodes: Partial<ContentItemViewNodesType>[],
            selectedProjectElementNode?: Partial<ProjectElementNode>
        ): ProjectElementNode[] => {
            // Early validation
            if (!selectedProjectElementNode?.isInProject) {
                return []
            }

            // Get all ancestor content items using findAncestors utility
            const ancestorContentItems = findAncestors(
                allContentNodes,
                contentItem
            )

            // Get PT element nodes with bulk cache for all ancestor content items
            const ptElementNodes = getPTElementNodesWithBulkCache(
                ancestorContentItems
            )
            if (ptElementNodes.length === 0) return []

            const inactivePTMap = new Map<string, ProjectElementNode>()

            ancestorContentItems.forEach((ancestorItem) => {
                // Filter to Paragraph nodes only (skip Articles, Sections)
                if (ancestorItem.textType !== 'Paragraph') {
                    return
                }

                const inactivePT = findInactivePTFromContentItem(
                    ancestorItem,
                    ptElementNodes
                )
                if (inactivePT) {
                    inactivePTMap.set(inactivePT.id, inactivePT)
                }
            })

            return Array.from(inactivePTMap.values())
        },
        [getPTElementNodesWithCache, getPTElementNodesWithBulkCache]
    )

    /**
     * Unified handler for PT activation (single or bulk).
     * Takes an array of inactive PTs, shows dialog, adds PTs, and activates all linked content.
     *
     * @param inactivePTs - Array of PT element nodes to activate (can be empty, single, or multiple)
     * @param contentItem - Content item that triggered the activation
     * @param selectedProjectElementNodeId - ID of selected project element
     * @param onComplete - Optional callback to execute after all activation is complete
     * @param activateOnlySpecificChild - If true, only activate the specific child clicked (child→parent flow)
     * @returns true if activation dialog shown, false if no action needed
     */
    const handlePTActivation = useCallback(
        (
            inactivePTs: ProjectElementNode[],
            contentItem: Partial<ContentItemViewNodesType>,
            selectedProjectElementNodeId?: string,
            onComplete?: () => void,
            activateOnlySpecificChild?: boolean
        ): boolean => {
            if (inactivePTs.length === 0) return false

            const ptNames = inactivePTs
                .map((pt) => extractTextBeforeColon(pt.text || ''))
                .filter((name) => name)
            const idsToRemove = new Set<string>()
            const onProceed = async () => {
                try {
                    // Step 1: Add all PTs to project (progress dialog will show during this)
                    await addPTsToProject(inactivePTs)

                    // Step 2: Collect all content to activate
                    const allItemsToActivate = collectPTLinkedContent(ptNames)

                    // Step 2b: Sync Articles from backend
                    // Fetches all Articles, compares with frontend, adds changed ones to activation list
                    await syncArticlesToActivationList(allItemsToActivate)

                    // Step 2c: Sync PT paragraph content (MFR, keyed content) from backend
                    // Bulk-fetches all PT article content, replaces PT paragraph → last child
                    // with fresh backend data (handles nodes not yet in frontend state).
                    // idsToRemove collects old frontend children that will be replaced by
                    // new backend nodes, preventing duplicate manufacturer nodes in the store.
                    await syncPTContentToActivationList(
                        allItemsToActivate,
                        ptNames,
                        idsToRemove
                    )

                    // If onComplete callback provided, handle activation scope
                    if (onComplete) {
                        if (activateOnlySpecificChild) {
                            // Child→Parent: Activate the specific child clicked + all ancestors up to PT parent
                            if (contentItem.id) {
                                allItemsToActivate.set(
                                    contentItem.id,
                                    contentItem
                                )
                            }

                            // Also add all ancestors of the clicked child (to activate non-PT parents in between)
                            const storeState = useWorkspace2Store.getState()
                            const latestNodes = storeState.contentItemViewNodes
                            const ancestors = findAncestors(
                                latestNodes,
                                contentItem
                            )
                            ancestors.forEach((ancestor) => {
                                if (ancestor.id && ancestor.isHidden) {
                                    allItemsToActivate.set(
                                        ancestor.id,
                                        ancestor
                                    )
                                }
                            })
                        } else {
                            // Parent→Child: Activate ALL children + ancestors (existing behavior)
                            const childrenAndAncestors = collectAllChildrenAndAncestors(
                                contentItem
                            )
                            childrenAndAncestors.forEach((item) => {
                                if (item.id) {
                                    allItemsToActivate.set(item.id, item)
                                }
                            })
                        }
                    }

                    // Step 3d: Filter out isBOD nodes and their children from ALL collected items
                    filterOutBODNodes(allItemsToActivate)

                    const finalItemsToActivate = Array.from(
                        allItemsToActivate.values()
                    )

                    // Step 4: Activate all collected items (single state update + bulk mutation)
                    if (finalItemsToActivate.length > 0) {
                        await activateContentWithRollback(
                            finalItemsToActivate,
                            selectedProjectElementNodeId,
                            idsToRemove
                        )
                    } else {
                        // No items to activate, close progress dialog manually
                        closeProgressDialog()
                        closePTActivationDialog()
                    }

                    // Step 5: Complete parent activation if callback provided
                    if (onComplete) {
                        onComplete()
                    }
                } catch (error) {
                    closeProgressDialog() // Close progress dialog on error
                    closePTActivationDialog() // Close main dialog on error
                    throw error
                }
            }

            // Show dialog
            openPTActivationDialog(
                inactivePTs[0],
                ptNames,
                contentItem.id ?? '',
                onProceed
            )

            return true
        },
        [
            addPTsToProject,
            openPTActivationDialog,
            closePTActivationDialog,
            closeProgressDialog,
            activateContentWithRollback,
            syncArticlesToActivationList,
            syncPTContentToActivationList,
            filterOutBODNodes,
        ]
    )

    /**
     * Checks if content deactivation should show PT warning (without opening dialog)
     */
    const shouldWarnPTDeactivation = useCallback(
        (
            selectedContentItemNode: Partial<ContentItemViewNodesType>,
            selectedProjectElementNode?: Partial<ProjectElementNode>
        ): boolean => {
            // Early validation
            if (!selectedProjectElementNode?.isInProject) {
                return false
            }

            if (selectedContentItemNode.textType !== 'Paragraph') {
                return false
            }

            const ptElementNodes = getPTElementNodes(elementList || [])
            if (ptElementNodes.length === 0) {
                return false
            }

            const paragraphText = selectedContentItemNode.elements?.[0]?.text
            if (!paragraphText) {
                return false
            }

            const contentPTName = extractTextBeforeColon(paragraphText)
            if (!contentPTName) return false

            const linkedActivePT = ptElementNodes.find((ptNode) => {
                const ptDisplayName = extractTextBeforeColon(ptNode.text || '')
                return (
                    ptDisplayName.toLowerCase() ===
                        contentPTName.toLowerCase() &&
                    ptNode.isInProject === true
                )
            })

            return !!linkedActivePT
        },
        [elementList]
    )

    /**
     * Checks if content deactivation needs PT warning and handles the flow
     */
    const checkAndHandlePTDeactivation = useCallback(
        (
            selectedContentItemNode: Partial<ContentItemViewNodesType>,
            selectedProjectElementNode?: Partial<ProjectElementNode>
        ): boolean => {
            if (
                !shouldWarnPTDeactivation(
                    selectedContentItemNode,
                    selectedProjectElementNode
                )
            ) {
                return false
            }

            const onProceed = async () => {
                try {
                    await deactivateSingleContent(
                        selectedContentItemNode,
                        selectedProjectElementNode?.id
                    )
                } catch (error) {
                    throw error
                }
            }

            openPTDeactivationDialog(onProceed)

            return true
        },
        [
            shouldWarnPTDeactivation,
            deactivateSingleContent,
            openPTDeactivationDialog,
        ]
    )

    /**
     * Helper function to check for inactive PTs and handle activation dialog
     * Returns true if PT activation was handled (dialog shown), false otherwise
     *
     * @param selectedContentItemNode - The content item node being activated
     * @param allContentNodes - All content nodes for tree traversal
     * @param selectedProjectElementNode - The selected project element node
     * @param isHidden - Whether the content is being hidden (true) or shown (false)
     * @param onComplete - Callback to execute after PT activation completes
     * @returns true if PT activation dialog was shown, false otherwise
     */
    const checkAndHandlePTActivationIfNeeded = useCallback(
        async (
            selectedContentItemNode: Partial<ContentItemViewNodesType>,
            allContentNodes: Partial<ContentItemViewNodesType>[],
            selectedProjectElementNode: Partial<ProjectElementNode> | undefined,
            isHidden: boolean,
            onComplete: () => void
        ): Promise<boolean> => {
            // Skip if deactivating or feature disabled
            if (isHidden) {
                return false
            }

            // Special handling for Reviewers
            if (role === 'Reviewer') {
                if (isDirectPTKeyParagraph(selectedContentItemNode)) {
                    openContributorRestrictionDialog()
                    return true
                }

                const inactiveParentPTs = detectInactiveParentPTs(
                    selectedContentItemNode,
                    allContentNodes,
                    selectedProjectElementNode
                )
                if (inactiveParentPTs.length > 0) {
                    openContributorRestrictionDialog()
                    return true
                }

                return false
            }

            // For collapsed articles: children may not be in frontend state yet.
            // Fetch backend content so detectInactivePTs can scan them for PT keys.
            let extendedContentNodes = allContentNodes
            if (
                selectedContentItemNode.textType === 'Article' &&
                selectedContentItemNode.id
            ) {
                const frontendChildren = getChildElements(
                    selectedContentItemNode,
                    allContentNodes
                )
                if (frontendChildren.length === 0) {
                    const backendNodes = await fetchChildrenForArticle(
                        selectedContentItemNode.id
                    )
                    if (backendNodes.length > 0) {
                        extendedContentNodes = [...allContentNodes, ...backendNodes]
                    }
                }
            }

            const inactiveChildPTs = detectInactivePTs(
                selectedContentItemNode,
                extendedContentNodes,
                selectedProjectElementNode
            )

            const inactiveParentPTs = detectInactiveParentPTs(
                selectedContentItemNode,
                extendedContentNodes,
                selectedProjectElementNode
            )

            const allInactivePTs = [...inactiveChildPTs, ...inactiveParentPTs]
            const uniqueInactivePTs = Array.from(
                new Map(allInactivePTs.map((pt) => [pt.id, pt])).values()
            )

            if (uniqueInactivePTs.length > 0) {
                const isChildToParentActivation = inactiveParentPTs.length > 0

                return handlePTActivation(
                    uniqueInactivePTs,
                    selectedContentItemNode,
                    selectedProjectElementNode?.id,
                    onComplete,
                    isChildToParentActivation
                )
            }

            return false
        },
        [
            role,
            isDirectPTKeyParagraph,
            openContributorRestrictionDialog,
            detectInactivePTs,
            detectInactiveParentPTs,
            handlePTActivation,
            fetchChildrenForArticle,
        ]
    )

    // PT key paragraph validation functions now provided by usePTKeyParagraphValidator hook

    return {
        // Activation API
        detectInactivePTs,
        detectInactiveParentPTs,
        handlePTActivation,
        checkAndHandlePTActivationIfNeeded,

        // Deactivation API
        shouldWarnPTDeactivation,
        checkAndHandlePTDeactivation,
    }
}
